import { useCallback } from 'react';

import { isHiddenBookmarkName, Queries } from '@aline/common';
import { equals } from 'ramda';
import { useEditorLoading, useSlateSelector } from 'src/layout-engine/hooks';

/** In the order the radio buttons appear, as in Word. */
export const BOOKMARK_SORT_ORDERS = ['name', 'location'] as const;

export type BookmarkSortOrder = (typeof BOOKMARK_SORT_ORDERS)[number];

export const BOOKMARK_SORT_ORDER_LABELS: Record<BookmarkSortOrder, string> = {
  location: 'Location',
  name: 'Name',
};

export interface BookmarkListItem {
  markerId: number;
  name: string;
}

export function isBookmarkSortOrder(value: string): value is BookmarkSortOrder {
  const sortOrders: readonly string[] = BOOKMARK_SORT_ORDERS;
  return sortOrders.includes(value);
}

/**
 * Collect the visible bookmarks in the open document, in document order unless sorted by name.
 */
export function useBookmarks(options: { open: boolean; sortOrder: BookmarkSortOrder }) {
  const { open, sortOrder } = options;
  const editorLoading = useEditorLoading();

  const bookmarks = useSlateSelector(
    useCallback(
      editor => {
        if (!open || editorLoading) return [];

        const bookmarksInDocumentOrder = Queries.getBookmarks(editor)
          .filter(([{ name }]) => !isHiddenBookmarkName(name))
          .map(([{ markerId, name }]): BookmarkListItem => ({ markerId, name }));

        if (sortOrder === 'location') return bookmarksInDocumentOrder;
        return bookmarksInDocumentOrder.toSorted((a, b) => a.name.localeCompare(b.name));
      },
      [editorLoading, open, sortOrder]
    ),
    equals
  );

  return { bookmarks };
}
